import { useEffect, useState } from 'react';
import { createHashRouter, RouterProvider, Navigate } from 'react-router';
import { Layout } from './Layout';
import { AREAS } from './areas';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { BackupPage } from '@/features/backup/BackupPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { InstallHelpPage } from '@/features/pwa/InstallHelpPage';
import { PlaceholderPage } from '@/features/placeholder/PlaceholderPage';
import { IngredientsPage } from '@/features/ingredients/IngredientsPage';
import { PurchasesPage } from '@/features/purchases/PurchasesPage';
import { NewPurchasePage } from '@/features/purchases/NewPurchasePage';
import { InventoryPage } from '@/features/inventory/InventoryPage';
import { RecipesPage } from '@/features/recipes/RecipesPage';
import { RecipeDetailPage } from '@/features/recipes/RecipeDetailPage';
import { MenuPage } from '@/features/menu/MenuPage';
import { ProductionPage } from '@/features/production/ProductionPage';
import { ShoppingListPage } from '@/features/shopping/ShoppingListPage';
import { SalesPage } from '@/features/sales/SalesPage';
import { ProfitPage } from '@/features/profit/ProfitPage';
import { OrdersPage } from '@/features/orders/OrdersPage';
import { CashPage } from '@/features/cash/CashPage';
import { ErrorScreen, RouteErrorScreen } from './ErrorScreen';
import { asegurarSettings, actualizarSettings } from '@/db/schema';

/** Areas que ya tienen pantalla propia; el resto usa marcador de posicion. */
const RUTAS_IMPLEMENTADAS: readonly string[] = [
  '/respaldos',
  '/insumos',
  '/compras',
  '/inventario',
  '/recetas',
  '/menu',
  '/produccion',
  '/lista-compras',
  '/ventas',
  '/ganancias',
  '/pedidos',
  '/caja',
];
import { solicitarPersistencia } from '@/lib/storage';

/**
 * Se usa enrutado por fragmento (#) y no por ruta.
 *
 * Motivo: la aplicacion se publica en GitHub Pages, donde una recarga directa
 * sobre una ruta profunda devolveria 404 porque no hay servidor que reescriba.
 * Con el fragmento el navegador nunca pide esa ruta al servidor, asi que
 * funciona con cualquier `base` y sin configuracion adicional. En modo
 * standalone la barra de direcciones no se ve, asi que el # no estorba.
 */
const router = createHashRouter([
  {
    path: '/',
    element: <Layout />,
    // Captura cualquier fallo dentro de la aplicacion y ofrece una salida en
    // lugar de dejar una pantalla sin escape (ver ErrorScreen).
    errorElement: <RouteErrorScreen />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'respaldos', element: <BackupPage /> },
      { path: 'ajustes', element: <SettingsPage /> },
      { path: 'instalar', element: <InstallHelpPage /> },

      // Fase 2
      { path: 'insumos', element: <IngredientsPage /> },
      { path: 'compras', element: <PurchasesPage /> },
      { path: 'compras/nueva', element: <NewPurchasePage /> },
      { path: 'inventario', element: <InventoryPage /> },

      // Fase 3
      { path: 'recetas', element: <RecipesPage /> },
      { path: 'recetas/:id', element: <RecipeDetailPage /> },

      // Fase 4
      { path: 'menu', element: <MenuPage /> },
      { path: 'produccion', element: <ProductionPage /> },
      { path: 'lista-compras', element: <ShoppingListPage /> },

      // Fase 5
      { path: 'ventas', element: <SalesPage /> },
      { path: 'ganancias', element: <ProfitPage /> },

      // Fase 6
      { path: 'pedidos', element: <OrdersPage /> },

      // Fase 7
      { path: 'caja', element: <CashPage /> },

      // Areas de fases posteriores, con marcador de posicion.
      ...AREAS.filter((area) => !RUTAS_IMPLEMENTADAS.includes(area.ruta)).map((area) => ({
        path: area.ruta.replace(/^\//, ''),
        element: <PlaceholderPage ruta={area.ruta} />,
      })),

      { path: '*', element: <Navigate to="/" replace /> },
    ],
  },
]);

export function App() {
  const [errorArranque, setErrorArranque] = useState<unknown>(null);

  /**
   * Arranque: crea la configuracion si no existe y solicita almacenamiento
   * persistente (Req. 1.6). Es idempotente, asi que el doble montaje de
   * StrictMode en desarrollo no causa problema.
   *
   * Se ejecuta despues del primer render a proposito: si la base de datos
   * fallara, se muestra un mensaje en lugar de una pantalla en blanco.
   */
  useEffect(() => {
    let vigente = true;

    void (async () => {
      try {
        await asegurarSettings();
        const persistente = await solicitarPersistencia();
        await actualizarSettings({ almacenamientoPersistente: persistente });
      } catch (error) {
        if (vigente) setErrorArranque(error);
      }
    })();

    return () => {
      vigente = false;
    };
  }, []);

  // Si la base de datos no abre, se muestra la misma pantalla de recuperacion:
  // tambien aqui hace falta una salida que no implique borrar la aplicacion.
  if (errorArranque !== null) {
    return <ErrorScreen error={errorArranque} />;
  }

  return <RouterProvider router={router} />;
}
